import I18N from '../../utils/I18nMessageProvider'
import { isAndroid, isiOS } from '../../utils/DeviceInfoHelper'
import { getNavigationAndroidLink, getNavigationFallbackLink, getNavigationIOSLink } from '../../utils/NavigationLinks'

const jsep = require('jsep')

const WHITELISTED_BINARY_EXPRESSIONS = [
  '+',
  '-',
  '*',
  '/',
  '===',
  '!=='
]

const WHITELISTED_LOGICAL_EXPRESSIONS = [
  '&&',
  '||'
]

const WHITELISTED_CALL_EXPRESSIONS = [
  'Number',
  'String',
  'Boolean',

  'back',
  'call',
  'concat',
  'centerUserPositionOnMap',
  'exists',
  'getUserPosition',
  'hideOverlay',
  'hideSetting',
  'html',
  'i18n',
  'openExternalWebsite',
  'route',
  'runsInIframe',
  'showDetails',
  'showOnMap',
  'showSettingAndOverlay',
  'showSetting',
  'text',
  'toggleSetting',
  'url',
  'userPositionAvailable',
  'rotateCategories',
  'categoryVisible'
]

class ConfigFunctions {

  constructor (state, getCurrentOuterState, setOuterState, updatePoisFn, showMapOrListFn, settingState) {
    this.functionsDeployed = false

    this.outerState = state
    this.getCurrentOuterState = getCurrentOuterState
    this.setOuterState = setOuterState
    this.showMapOrListFn = showMapOrListFn
    this.updatePoisFn = updatePoisFn
    this.userPositionAvailableFn = null     // will be set via set method
    this.showPoiDetailsFn = null     // will be set via set method

    /**
     * @type {SettingsState}
     */
    this.settingState = settingState

    /**
     *
     * @type {LeafletControlInterface}
     */
    this.leafletController = null   // will be set via set method
    this.centerUserPositionOnMapFn = null   // will be set via set method

    this.currentRotationIndex = 0

    this.context = {
      back: this.showMapOrListFn,
      call: this._call,
      concat: this._concat,
      centerUserPositionOnMap: this._centerUserPositionOnMap,
      exists: this._exists,
      getUserPosition: this._getUserPosition,
      hideOverlay: this._hideOverlay,
      hideSetting: this._hideSetting,
      html: this._html,
      i18n: I18N.get,
      openExternalWebsite: this._openWebsite,
      route: this._route,
      runsInIframe: this._runsInIframe,
      showDetails: this._showDetails,
      showOnMap: this._showOnMap,
      showSettingAndOverlay: this._showSettingAndOverlay,
      showSetting: this._showSetting,
      text: this._string,
      toggleSetting: this._toggleSetting,
      url: this._string,
      userPositionAvailable: this._userPositionAvailable,
      rotateCategories: this._rotateCategories,
      categoryVisible: this._categoryVisible
    }
  }

  deployFunctionsToWindow () {
    Object.keys(this.context).forEach(key => {
      window[key] = this.context[key]
    })
    this.functionsDeployed = true
  }

  _centerUserPositionOnMap = () => {
    if (this.centerUserPositionOnMapFn) {
      this.centerUserPositionOnMapFn()
    }
  }

  _call = (poi) => {
    if (poi && poi.phone) {
      this._openWebsite('tel:' + poi.phone.replace(/\s+/g, ''))
    }
  }

  _concat = (...args) => {
    return args.join('')
  }

  _exists (arg) {
    return arg !== null && arg !== undefined
  }

  _getUserPosition = () => {
    if (this.leafletController) {
      return this.leafletController.getUserPosition()
    }
  }

  _hideOverlay = () => {
    this.setOuterState({overlayShown: true, showOverlay: false})
  }

  _hideSetting = (settingId) => {
    this.settingState.setVisible(settingId, false)
    this.setOuterState({settingState: this.settingState})
  }

  _html = (text) => {
    return text
      .replace(/<br\/>/g, '\n')
  }

  _runsInIframe = () => {
    return window.KesiTools && window.KesiTools.runsInIframe ? window.KesiTools.runsInIframe() : false
  }

  /**
   *
   * @param url
   * @param external no influence if running in an iframe
   * @private
   */
  _openWebsite = (url, external = true) => {
    if (this._runsInIframe()) {
      window.KesiTools.openWebsiteInSystemBrowser(url)
    }
    else if (external) {
      const win = window.open(url, '_blank')
      win.focus()
    }
    else {
      window.location.href = url
    }
  }

  _route = (poi) => {
    let url
    const name = poi.name
    const position = poi.getPosition()
    let external = true
    const kesiOS = window.KesiTools ? window.KesiTools.getOS() : null
    if (isAndroid() || kesiOS === 'android') {
      url = getNavigationAndroidLink(name, position)
      external = false
    }
    else if (isiOS() || kesiOS === 'ios') {
      url = getNavigationIOSLink(name, position)
    }
    else {
      url = getNavigationFallbackLink(name, position)
    }
    this._openWebsite(url, external)
  }

  _showDetails = (poi) => {
    if (this.showPoiDetailsFn) {
      this.showPoiDetailsFn(poi)
    }
  }

  _showSettingAndOverlay = (settingId) => {
    this.settingState.setVisible(settingId, true)
    this.setOuterState({showOverlay: true, settingState: this.settingState})
  }

  _showSetting = (settingId) => {
    this.settingState.setVisible(settingId, true)
    this.setOuterState({settingState: this.settingState})
  }

  _showOnMap = (poi) => {
    if (this.leafletController) {
      this.showMapOrListFn(false)
      setTimeout(() => {
        // ios workaround to set focus back on map
        // otherwise double click is needed to show details from popup again
        if (document.activeElement === document.body) {
          const containers = document.getElementsByClassName('leaflet-container')
          if (containers.length > 0) {
            containers[0].click()
          }
        }

        this.leafletController.showPopup(poi)
      })
    }
  }

  _string (text) {
    return text
  }

  _toggleSetting = (category) => {
    if (category) {
      // console.log('before', JSON.stringify(this.outerState.filterSettings.visibility[category]))
      this.outerState.filterSettings.visibility[category] = !this.outerState.filterSettings.visibility[category]
      // console.log('after', JSON.stringify(this.outerState.filterSettings.visibility[category]))
      this.updatePoisFn()
    }
  }

  _rotateCategories = (categories) => {
    if (categories && categories.length > 0) {
      const currentIndex = this.currentRotationIndex
      const nextIndex = (currentIndex + 1) % categories.length

      // turn off all categories of previous config that are not in next config
      const categoriesToHide = []
      const categoriesToShow = []
      categories[currentIndex].forEach(category => {
        if (categories[nextIndex].indexOf(category) < 0) {
          categoriesToHide.push(category)
        }
      })

      // turn on all categories of next config that are not in previous config
      categories[nextIndex].forEach(category => {
        if (categories[currentIndex].indexOf(category) < 0) {
          categoriesToShow.push(category)
        }
      })

      categoriesToHide.forEach(category => {
        window.setCategoryVisibleFn(category, false)
        this.outerState.filterSettings.visibility[category] = false
      })
      categoriesToShow.forEach(category => {
        window.setCategoryVisibleFn(category, true)
        this.outerState.filterSettings.visibility[category] = true
      })

      this.currentRotationIndex = nextIndex
    }
  }

  _categoryVisible = (category) => {
    if (category) {
      return this.getCurrentOuterState().filterSettings.visibility[category]
    }
  }

  _userPositionAvailable = () => {
    return window.KesiTools && window.KesiTools.geoLocationAvailable()
    // return this.leafletController ? this.leafletController.hasUserPosition() : true  // optimistic approach
  }

  checkAstSubtree (astTree) {
    if (!astTree) {
      console.warn('not accepting astTree which is null - probably something went wrong on parsing')
      return false
    }

    let allowed
    let reason
    switch (astTree.type) {
      case 'UnaryExpression':
        allowed = astTree.operator === '!'
        reason = `Not accepting ast type ${astTree.type} because its operator is not whitelisted`
        break

      case 'CallExpression':
        allowed = astTree.callee && WHITELISTED_CALL_EXPRESSIONS.indexOf(astTree.callee.name) >= 0
        reason = `Not accepting ast type ${astTree.type} because its callee is not whitelisted`
        break

      case 'MemberExpression':
        allowed = astTree.object && astTree.object.name === 'poi'    // this is the only whitelisted object
        reason = `Not accepting ast member access to ${astTree.object} as it is not whitelisted`
        break

      case 'BinaryExpression':
        allowed = WHITELISTED_BINARY_EXPRESSIONS.indexOf(astTree.operator) >= 0 && this.checkAstSubtree(astTree.left) && this.checkAstSubtree(astTree.right)
        reason = `Not accepting ast binary expression ${astTree.operator} or because subtrees are not accepted`
        break

      case 'ConditionalExpression':
        allowed = this.checkAstSubtree(astTree.alternate) && this.checkAstSubtree(astTree.consequent) && this.checkAstSubtree(astTree.test)
        reason = 'Not accepting ast conditional expression because one of the subtrees are not accepted (either alternate, consequent or test)'
        break

      case 'Literal':
        allowed = true
        break

      case 'LogicalExpression':
        allowed = WHITELISTED_LOGICAL_EXPRESSIONS.indexOf(astTree.operator) >= 0 && this.checkAstSubtree(astTree.left) && this.checkAstSubtree(astTree.right)
        reason = 'Not accepting ast logical expression because one of the subtrees are not accepted (either alternate, consequent or test)'
        break

      default:
        allowed = false
        reason = `Not accepting ast type ${astTree.type}`
    }

    if (!allowed) {
      console.warn(reason, astTree)
    }
    return allowed
  }

  checkAst (configValue) {
    const jsepAst = jsep(configValue)
    // console.log({configValue, jsepAst})

    const accepted = this.checkAstSubtree(jsepAst)
    if (!accepted) {
      console.warn('not accepting config value', configValue)
    }

    return accepted
  }

  eval (configValue, poi, forceEval = false, setCategoryVisibleFn = null) {
    if (!forceEval && !this.functionsDeployed) {
      console.warn('config value function not yet deployed to window. Config value: ', configValue)
    }
    else {
      try {
        if (this.checkAst(configValue)) {
          window.poi = poi
          window.setCategoryVisibleFn = setCategoryVisibleFn
          return eval(configValue)
        }
      }
      catch (error) {
        console.warn(`error evaluting configValue ${configValue}`, error)
      }
    }

    return null
  }

  setCenterUserPositionOnMapFn (fn) {
    this.centerUserPositionOnMapFn = fn
  }

  setShowPoiDetailsFn (fn) {
    this.showPoiDetailsFn = fn
  }

  setLeafletController (leafletController) {
    this.leafletController = leafletController
  }

}

export default ConfigFunctions



// WEBPACK FOOTER //
// ./src/components/TripAdvisor/ConfigFunctions.js